import React, { useMemo } from "react";
import { useHideOverflowing } from "../common/useHideOverflowing";
import { useTranslation } from 'react-i18next';
import { AttentionIcon, CopyToClipboard, RefreshSpinner } from "../common/ProjectIcons";
import { toast } from "react-toastify";
import { Destination, Source } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import Pagination from "../common/Pagination";
import { page_size } from "../../api/apiUtils";
import { useTimezone } from "../../context/TimezoneContext";
import { formatSourceDatetime } from "../sources/SourceHelperFunctions";
import { tableClasses, paginationClasses, filterClasses, buttonClasses, onTealText } from "../common/tailwindClassnames";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";
import SortByDropdown, { SortOption } from "../common/SortByDropdown";

type FilterOption = { label: string; value: string };

type RouteSourceProps = {
    sources: Source[],
    totalSources: number,
    globalSearchStr: string,
    take: number,
    offset: number,
    onPageClicked: Function,
    loadingSources: boolean,
    pageLoaded: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    destinationUrlFilterValue: FilterOption | null,
    onChangeGlobalSourceSearch: (value: string) => void,
    onRefresh: () => void,
    onChangeDestinationUrlFilter: Function,
    // Opens one of the Route's destinations. The Route owns the navigation —
    // the URL it builds keeps the id the page is addressed by.
    onViewDestination: (destinationId: string) => void,
    ordering: string,
    onChangeSortBy: (selected: SortOption) => void,
}

const RouteDetailsSource = ({
    sources,
    totalSources,
    globalSearchStr,
    take,
    offset,
    onPageClicked,
    pageLoaded,
    loadingSources,
    destinationUrls,
    loadingDestinationUrls,
    destinationUrlFilterValue,
    onChangeGlobalSourceSearch,
    onRefresh,
    onChangeDestinationUrlFilter,
    onViewDestination,
    ordering,
    onChangeSortBy,
}: RouteSourceProps) => {
    // Phones: a control that does not fully fit in its row is hidden, not cut in half.
    const searchRowRef = useHideOverflowing<HTMLDivElement>();
    const filterRowRef = useHideOverflowing<HTMLDivElement>();
    const { t } = useTranslation();
    const { timezone } = useTimezone();

    const sortOptions: SortOption[] = useMemo(() => [
        { label: t("External ID (A-Z)"), value: "external_id" },
        { label: t("External ID (Z-A)"), value: "-external_id" },
        { label: t("Newest First"), value: "-created_at" },
        { label: t("Oldest First"), value: "created_at" },
    ], [t]);

    const hasActiveFilters = !!(globalSearchStr || destinationUrlFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSourceSearch('');
        onChangeDestinationUrlFilter(null);
    };

    return (
        <div className="mt-8">
            <div className="flex flex-row justify-between items-center w-full">
                <label className={`${onTealText} transition-colors duration-300`}>{t("The individual sets of data coming from the provider.")}</label>
            </div>
            <div className="flex flex-col my-3">
                {/* Search row */}
                <div ref={searchRowRef} className="flex flex-row flex-nowrap items-end gap-3 mb-3 overflow-x-clip">
                    <GlobalSearch onChangeGlobalSearch={onChangeGlobalSourceSearch} placeholder={t("Name")} savedString={globalSearchStr} />
                    <SortByDropdown options={sortOptions} ordering={ordering} onChangeSortBy={onChangeSortBy} />
                    <button
                        type="button"
                        onClick={() => onRefresh()}
                        aria-label={t("Refresh list")}
                        disabled={loadingSources}
                        className={`flex justify-center items-center w-8 h-[38px] cursor-pointer self-end mb-2 ml-auto ${loadingSources ? 'animate-spin origin-center' : ''} transition-colors duration-300`}
                    >
                        <RefreshSpinner />
                    </button>
                    {hasActiveFilters && (
                        <button className={`${buttonClasses.clearFilters} whitespace-nowrap mb-2`} onClick={clearAllFilters}>
                            {t("Clear All Filters")}
                        </button>
                    )}
                </div>
                {/* Filter dropdowns row */}
                <div ref={filterRowRef} className="flex flex-row gap-3 mb-3 flex-nowrap overflow-x-clip">
                    <div className="flex flex-col gap-1">
                        <span className={filterClasses.label}>{t("Destination")}</span>
                        <SelectInput
                            name="destination_url__in"
                            options={destinationUrls.map(url => {
                                let hostname = url;
                                try { hostname = new URL(url).hostname; } catch {}
                                return { label: hostname, value: url };
                            })}
                            value={destinationUrlFilterValue}
                            onChange={onChangeDestinationUrlFilter}
                            isMulti={false}
                            isClearable={true}
                            isSearchable={true}
                            isDisabled={false}
                            isLoading={loadingDestinationUrls}
                            className="w-48"
                            variant="routeFilter"
                        />
                    </div>
                </div>
                <div className="flex flex-col w-full">
                    <label className={`${onTealText} transition-colors duration-300`}>{t("Total Sources")}: {totalSources}</label>
                    <div className="overflow-x-auto">
                    <table className={`mt-3 ${tableClasses.container} cursor-pointer`}>
                            <thead className={tableClasses.header}>
                                <tr>
                                    <th className={tableClasses.headerCell}>
                                        {t("External ID")}
                                    </th>
                                    <th className={tableClasses.headerCell}>
                                        {t("Destination")}
                                    </th>
                                    <th className={tableClasses.headerCell}>
                                        {t("Date Created")}
                                    </th>
                                </tr>
                            </thead>
                            {loadingSources ? <tbody className="text-sm w-full" role="status" aria-busy="true">
                                <tr className="sr-only"><td>Loading...</td></tr>
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className={tableClasses.row}>
                                        <td className={tableClasses.cell}><div className={`h-4 ${i % 3 === 0 ? 'w-44' : i % 3 === 1 ? 'w-32' : 'w-52'} bg-teal-t30 rounded animate-pulse`} /></td>
                                        <td className={tableClasses.cell}><div className={`h-4 ${i % 3 === 0 ? 'w-48' : i % 3 === 1 ? 'w-36' : 'w-40'} bg-teal-t30 rounded animate-pulse`} /></td>
                                        <td className={tableClasses.cell}><div className="h-4 w-32 bg-teal-t30 rounded animate-pulse" /></td>
                                    </tr>
                                ))}
                            </tbody> : sources.length < 1 ? <tbody><tr><td colSpan={3} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to configure your first source with this Route.")}
                                />
                            </td></tr></tbody> : <tbody className="text-sm">
                                {sources.map((source: Source) => {
                                    return (
                                        <tr key={source.id} className={tableClasses.row}>
                                            <td className={tableClasses.cell}>
                                                {source.status === "healthy" ? "" : <AttentionIcon />}{source.external_id}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {source.destinations.map((dest: Destination) => {
                                                    let dest_url: string;
                                                    try {
                                                        dest_url = new URL(dest.base_url).hostname
                                                    } catch (err) {
                                                        dest_url = ""
                                                    }
                                                    return (
                                                        <div key={dest.id} className="flex items-center gap-2 text-brand-accent-green">
                                                            {/* The hostname has always been drawn in link colour; it
                                                                now goes where every other "open this destination from
                                                                a Route" click goes — the account on the flow map. */}
                                                            <button
                                                                type="button"
                                                                data-testid={`RouteDetailsSources-destination-${dest.id}`}
                                                                className="cursor-pointer hover:underline text-left"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onViewDestination(dest.id);
                                                                }}
                                                            >
                                                                {dest_url}
                                                            </button>
                                                            {dest.base_url && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigator.clipboard.writeText(dest.base_url);
                                                                        toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                            position: toast.POSITION.TOP_RIGHT,
                                                                            autoClose: 2000
                                                                        });
                                                                    }}
                                                                    className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                                    aria-label="Copy URL to clipboard"
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </td>
                                            <td className={tableClasses.cell}>
                                                {formatSourceDatetime(source.created_at, timezone)}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>}
                        </table>
                    </div>
                    {totalSources > page_size && <Pagination
                        showText={true}
                        handlePageClick={onPageClicked}
                        pageCount={Math.ceil(totalSources / take)}
                        resultStart={offset + 1}
                        resultEnd={(offset + take) < totalSources ? (offset + take) : totalSources}
                        totalCount={totalSources}
                        take={take}
                        loading={!pageLoaded}
                        mainclassname={paginationClasses.container}
                    />}
                </div>
            </div>
        </div>
    )
};

export default RouteDetailsSource;
